import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import ShareIcon from "@mui/icons-material/Share";
import SegmentIcon from "@mui/icons-material/Segment";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import AddIcon from "@mui/icons-material/Add";
import { db } from "./firebase";
import { useSnackbar } from "./SnackbarHandler";
import MyRubricsTable from "./MyRubricsTable";
import Logger from "./Logger";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import SharedRubricsTable from "./SharedRubricsTable";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";

const MyRubrics = ({ user, styles, theme }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [myRubrics, setMyRubrics] = useState([]);
  const [sharedRubrics, setSharedRubrics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showSnackbar } = useSnackbar();
  const [allRubrics, setAllRubrics] = useState([]);

  useEffect(() => {
    fetchRubrics();
  }, [user.uid]);

  const fetchRubrics = async () => {
    setIsLoading(true);
    try {
      const adminIds = [
        "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
        "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
        "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
      ];

      if (user.admin) {
        // This query will get all rubrics where userId is NOT in the adminIds array
        const allRubricsQuery = query(
          collection(db, "rubrics"),
          where("userId", "not-in", adminIds),
          orderBy("createdAt", "desc"),
        );

        const allRubricsSnapshot = await getDocs(allRubricsQuery);
        const allRubricsList = allRubricsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllRubrics(allRubricsList);
      }

      // Fetch user's own rubrics
      const userRubricsQuery = query(
        collection(db, "rubrics"),
        where("userId", "==", user.uid),

        orderBy("createdAt", "desc"),
      );
      const userRubricsSnapshot = await getDocs(userRubricsQuery);
      const userRubricsList = userRubricsSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((doc) => !doc.shared);
      setMyRubrics(userRubricsList);

      // Fetch rubrics shared with the user
      const sharedRubricsQuery = query(
        collection(db, "rubrics"),
        where("sharedTo", "==", user.uid),
        orderBy("createdAt", "desc"),
      );
      const sharedRubricsSnapshot = await getDocs(sharedRubricsQuery);
      const sharedRubricsList = sharedRubricsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSharedRubrics(sharedRubricsList);
      Logger.log("sharedRubricsList: ", sharedRubricsList);
    } catch (error) {
      console.error("Error fetching rubrics:", error);
      showSnackbar(
        "Ett fel uppstod vid hämtning av bedömningsmatriser",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ ...styles.container }}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <Typography
          variant="h1"
          sx={{ ...styles.header, borderBottom: "0px", mb: 6 }}
        >
          Mina bedömningsmatriser
        </Typography>

        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            component={Link}
            startIcon={<AddIcon />}
            to="/app/matris/ny"
          >
            Ny matris
          </Button>
          <Button
            variant="purp"
            startIcon={<GenerateIcon />}
            component={Link}
            to={`/app/uppsats/ny`}
          >
            Ny bedömning
          </Button>
          <Button
            variant="purp"
            sx={{ ...styles.tightButton }}
            component={Link}
            startIcon={<SegmentIcon />}
            to="/app/uppsatser"
            sx={{ display: "none" }}
          >
            Dina bedömningar
          </Button>
        </Box>

        <Box
          sx={{
            ...styles.freshBox,
            padding: "0px",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              background: theme.palette.mode === "dark" ? "#22303c" : "#f9f9fa",
              borderTopLeftRadius: "9px",
              borderTopRightRadius: "9px",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  padding: "20px",
                  textTransform: "none",
                  fontSize: "1rem",
                },
              }}
            >
              <Tab
                icon={<TableChartIcon sx={{ color: "primary.main" }} />}
                iconPosition="start"
                label="Mina matriser"
              />
              <Tab
                icon={<ShareIcon sx={{ color: "#4CAF50" }} />}
                iconPosition="start"
                label="Delade med mig"
              />
              {user.admin && (
                <Tab
                  icon={<PeopleIcon sx={{ color: "#FF9800" }} />}
                  iconPosition="start"
                  label="Användarnas matriser"
                />
              )}
            </Tabs>
          </Box>

          <Box sx={{ paddingTop: "30px", paddingBottom: "30px" }}>
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <MyRubricsTable
                    rubrics={myRubrics}
                    onRubricsUpdated={fetchRubrics}
                    user={user}
                  />
                )}
                {activeTab === 1 && (
                  <SharedRubricsTable
                    rubrics={sharedRubrics}
                    onRubricsUpdated={fetchRubrics}
                    user={user}
                  />
                )}
                {activeTab === 2 && user.admin && (
                  <MyRubricsTable
                    rubrics={allRubrics}
                    onRubricsUpdated={fetchRubrics}
                    user={user}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyRubrics;
